import type { CSSProperties, ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
  type ModuleSectionNavProps,
} from "./ModuleSectionNav";

type DetailSectionHeaderBackAction =
  | {
      kind: "link";
      to: string;
    }
  | {
      kind: "button";
      onClick: () => void;
    };

type DetailSectionHeaderProps = {
  back: DetailSectionHeaderBackAction;
  items: ModuleSectionNavItem[];
  activeId?: string | null;
  ariaLabel: string;
  compact: boolean;
  isCompactLayout: boolean;
  showBackText: boolean;
  actions: ReactNode;
  className?: string;
  leadingClassName?: string;
  actionsClassName?: string;
  navContainerClassName?: string;
  navContainerTestId?: string;
  style?: CSSProperties;
  desktopClassName?: string;
  desktopBordered?: boolean;
  mobileTriggerFullWidth?: boolean;
  triggerClassName?: string;
  testIds?: ModuleSectionNavProps["testIds"];
};

const joinClassNames = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

export const DetailSectionHeader = ({
  back,
  items,
  activeId,
  ariaLabel,
  compact,
  isCompactLayout,
  showBackText,
  actions,
  className,
  leadingClassName,
  actionsClassName,
  navContainerClassName,
  navContainerTestId,
  style,
  desktopClassName,
  desktopBordered,
  mobileTriggerFullWidth,
  triggerClassName,
  testIds,
}: DetailSectionHeaderProps) => {
  const resolvedLeadingClassName =
    leadingClassName ??
    (compact
      ? "flex w-full min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center"
      : isCompactLayout
        ? "flex items-center gap-2 flex-1 min-w-0"
        : "flex items-center gap-4 md:gap-8 flex-1 min-w-0");
  const resolvedActionsClassName =
    actionsClassName ??
    (compact
      ? "mb-0 flex w-full flex-wrap items-center justify-start gap-1.5 sm:justify-end"
      : "mb-[0.3rem] gap-2");
  const resolvedClassName =
    className ??
    joinClassNames(
      "mt-4 mb-6 flex flex-shrink-0 justify-between border-b border-gray-200",
      compact ? "flex-wrap items-start gap-3" : "items-center",
    );
  const backClassName = joinClassNames(
    "flex items-center gap-1 text-[13px] font-bold text-irongrey/40 transition-colors hover:text-primary",
    compact ? "pb-0" : "pb-4",
  );

  const backControl =
    back.kind === "link" ? (
      <Link to={back.to} className={backClassName} aria-label="Back">
        <ChevronLeft size={16} />
        {showBackText ? <span>Back</span> : null}
      </Link>
    ) : (
      <button
        type="button"
        className={backClassName}
        aria-label="Back"
        onClick={back.onClick}
      >
        <ChevronLeft size={16} />
        {showBackText ? <span>Back</span> : null}
      </button>
    );

  return (
    <header className={resolvedClassName} style={style}>
      <div className={resolvedLeadingClassName}>
        {backControl}

        <div
          className={joinClassNames("flex-1 min-w-0", navContainerClassName)}
          data-testid={navContainerTestId}
        >
          <ModuleSectionNav
            items={items}
            activeId={activeId}
            ariaLabel={ariaLabel}
            compact={compact}
            desktopClassName={desktopClassName}
            desktopBordered={desktopBordered}
            mobileTriggerFullWidth={mobileTriggerFullWidth}
            triggerClassName={triggerClassName}
            testIds={testIds}
          />
        </div>
      </div>

      <div
        className={joinClassNames(
          "flex items-center shrink-0",
          resolvedActionsClassName,
        )}
      >
        {actions}
      </div>
    </header>
  );
};
